// In-place text editing (spec §5.4). A contenteditable element is laid over
// the object with the same transform the canvas uses, so the browser handles
// caret, selection, IME and spellcheck natively. On blur/Escape the DOM is
// serialised to RichText and committed as a single op.
import type { BoardObject, RichText } from '../../shared/types.ts';
import { isEmptyRichText, richTextEquals, sanitizeHref, truncateRichText } from '../../shared/rich-text.ts';
import type { Editor } from '../editor.ts';
import { h } from '../util/dom.ts';
import { aabb, objectMatrix } from '../util/geometry.ts';
import { icons } from '../ui/icons.ts';
import { linkHtml, richTextFromDom, richTextToHtml } from './html.ts';
import { FONT_FAMILY, LINE_HEIGHT } from './layout.ts';
import { canvasMeasure, isTextBearing, layoutFor, textSpec, TEXT_COLOR, type TextBearing } from './placement.ts';

export type CaretPlacement = 'end' | 'all' | { clientX: number; clientY: number };

export interface EditOptions {
  caret?: CaretPlacement;
  /** The caller opened a history gesture (e.g. object creation) that editing should close. */
  gesture?: boolean;
  /** The object was just created for this edit; an empty text object is discarded. */
  isNew?: boolean;
}

interface Session {
  id: string;
  wrapper: HTMLDivElement;
  content: HTMLDivElement;
  gesture: boolean;
  isNew: boolean;
  unsubscribe: () => void;
}

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
const MAX_CHARS = 8000; // soft cap while typing; commit also truncates to the byte limit
const CLIPBOARD_OBJECTS = '{"kind":"atomic-lab/objects"';

export class TextEditor {
  private readonly ed: Editor;
  private readonly container: HTMLElement;
  private session: Session | null = null;
  private committing = false;

  private readonly bar: HTMLDivElement;
  private readonly buttons: Record<'bold' | 'italic' | 'list' | 'link', HTMLButtonElement>;
  private readonly linkBox: HTMLDivElement;
  private readonly linkInput: HTMLInputElement;
  private savedRange: Range | null = null;

  constructor(editor: Editor, container: HTMLElement) {
    this.ed = editor;
    this.container = container;

    const button = (label: string, html: string, onClick: () => void) => {
      const b = h('button', { type: 'button', class: 'tb-btn', title: label, 'aria-label': label, 'aria-pressed': 'false' });
      b.innerHTML = html;
      b.addEventListener('mousedown', (e) => e.preventDefault()); // keep focus in the text
      b.addEventListener('click', onClick);
      return b;
    };
    this.buttons = {
      bold: button(`Bold (${isMac ? '⌘' : 'Ctrl+'}B)`, '<b>B</b>', () => this.exec('bold')),
      italic: button(`Italic (${isMac ? '⌘' : 'Ctrl+'}I)`, '<i>I</i>', () => this.exec('italic')),
      list: button(`Bullet list (${isMac ? '⇧⌘' : 'Ctrl+Shift+'}8)`, icons.list, () => this.exec('insertUnorderedList')),
      link: button(`Link (${isMac ? '⌘' : 'Ctrl+'}K)`, icons.link, () => this.openLink()),
    };
    this.bar = h('div', { class: 'panel text-format', role: 'toolbar', 'aria-label': 'Text formatting', hidden: '' },
      Object.values(this.buttons));

    this.linkInput = h('input', { type: 'text', inputmode: 'url', placeholder: 'Paste a link, Enter to apply', 'aria-label': 'Link address', spellcheck: 'false' });
    this.linkBox = h('div', { class: 'panel link-edit', hidden: '' }, [this.linkInput]);
    this.linkInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.applyLink(); }
      else if (e.key === 'Escape') { e.preventDefault(); this.closeLink(true); }
    });
    this.linkInput.addEventListener('blur', (e) => {
      if (!document.hasFocus() || this.ownsFocusTarget(e.relatedTarget)) return;
      this.closeLink(false);
      this.commit();
    });

    container.append(this.bar, this.linkBox);
    document.addEventListener('selectionchange', () => this.syncBar());
  }

  get editingId(): string | null {
    return this.session?.id ?? null;
  }

  start(id: string, opts: EditOptions = {}): void {
    if (this.session) this.commit();
    const o = this.ed.store.get(id);
    if (!o || !isTextBearing(o) || o.locked) {
      if (opts.gesture) this.ed.endGesture();
      return;
    }

    const content = h('div', {
      class: 'text-edit-content', contenteditable: 'true', role: 'textbox',
      'aria-multiline': 'true', 'aria-label': 'Edit text', spellcheck: 'true',
    });
    content.innerHTML = richTextToHtml(o.text);
    const box = h('div', { class: 'text-edit-box' }, [content]);
    const wrapper = h('div', { class: `text-edit text-edit-${o.type}` }, [box]);
    this.container.append(wrapper);

    const spec = textSpec(o);
    Object.assign(box.style, {
      left: `${spec.box.x}px`, top: `${spec.box.y}px`, width: `${spec.box.w}px`,
      height: o.type === 'text' ? 'auto' : `${spec.box.h}px`,
      justifyContent: spec.valign === 'middle' ? 'center' : 'flex-start',
    });
    Object.assign(content.style, {
      fontFamily: FONT_FAMILY, lineHeight: String(LINE_HEIGHT), color: TEXT_COLOR, textAlign: spec.align,
    });

    this.session = {
      id, wrapper, content,
      gesture: !!opts.gesture, isNew: !!opts.isNew,
      unsubscribe: combine(
        this.ed.events.on('camera', () => this.position()),
        // Someone else moved, resized or deleted it: follow, or stop editing.
        this.ed.store.subscribe((ids) => { if (ids.has(id)) this.position(); }),
      ),
    };
    this.ed.renderer.setEditing(id);

    content.addEventListener('input', () => this.onInput());
    content.addEventListener('keydown', (e) => this.onKeyDown(e));
    content.addEventListener('blur', (e) => {
      // Switching windows blurs too; editing resumes when focus returns.
      if (!document.hasFocus() || this.ownsFocusTarget(e.relatedTarget)) return;
      this.commit();
    });
    content.addEventListener('paste', (e) => this.onPaste(e));
    content.addEventListener('drop', (e) => e.preventDefault());
    content.addEventListener('beforeinput', (e) => {
      if (e.inputType.startsWith('insert') && (content.textContent?.length ?? 0) >= MAX_CHARS) e.preventDefault();
    });

    this.fitFont(o, o.text);
    this.position();
    content.focus({ preventScroll: true });
    document.execCommand('defaultParagraphSeparator', false, 'div');
    document.execCommand('styleWithCSS', false, 'false');
    placeCaret(content, opts.caret ?? 'end');
    this.bar.hidden = false;
    this.positionBar();
    this.syncBar();
  }

  /** Ends editing, committing any change as one op. */
  commit(): void {
    const s = this.session;
    if (!s || this.committing) return;
    this.committing = true;
    try {
      const rt = truncateRichText(richTextFromDom(s.content));
      this.teardown();
      const ed = this.ed;
      const o = ed.store.get(s.id);
      if (o && isTextBearing(o)) {
        if (o.type === 'text' && isEmptyRichText(rt)) {
          if (s.isNew && s.gesture) {
            ed.cancelGesture(); // discard the never-used text object entirely
            return;
          }
          ed.apply([{ t: 'delete', id: o.id }]);
        } else if (!richTextEquals(rt, o.text)) {
          const props: Partial<BoardObject> = { text: rt };
          if (o.type === 'text') props.h = layoutFor(o, rt, canvasMeasure).height;
          ed.apply([{ t: 'update', id: o.id, props }]);
        }
      }
      if (s.gesture) ed.endGesture();
    } finally {
      this.committing = false;
    }
  }

  /** Ends editing without touching the document (e.g. the object went away). */
  private teardown(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    s.unsubscribe();
    this.closeLink(false);
    this.bar.hidden = true;
    // Blur before removal so the browser finalises any in-progress IME composition.
    if (s.content.contains(document.activeElement)) s.content.blur();
    s.wrapper.remove();
    this.ed.renderer.setEditing(null);
  }

  private ownsFocusTarget(target: EventTarget | null): boolean {
    return target instanceof Node && !!this.session &&
      (this.session.content.contains(target) || this.bar.contains(target) || this.linkBox.contains(target));
  }

  private object(): TextBearing | null {
    const o = this.session && this.ed.store.get(this.session.id);
    return o && isTextBearing(o) ? o : null;
  }

  /** Keeps the overlay glued to the object as the camera moves. */
  private position(): void {
    const s = this.session;
    const o = this.object();
    if (!s) return;
    if (!o) {
      this.teardown();
      return;
    }
    const cam = this.ed.camera;
    const m = objectMatrix(o, cam.zoom, -cam.x * cam.zoom, -cam.y * cam.zoom);
    Object.assign(s.wrapper.style, { width: `${o.w}px`, height: `${o.h}px`, transform: `matrix(${m.join(',')})` });
    this.positionBar();
  }

  /** Stickies and shapes shrink their font to fit, exactly as the canvas layout does. */
  private fitFont(o: TextBearing, text: RichText | undefined): void {
    const s = this.session;
    if (!s) return;
    const layout = layoutFor(o, text ?? { blocks: [] }, canvasMeasure);
    s.content.style.fontSize = `${layout.fontSize}px`;
  }

  private onInput(): void {
    const s = this.session;
    const o = this.object();
    if (!s || !o) return;
    if (o.type !== 'text') this.fitFont(o, richTextFromDom(s.content));
    this.positionBar();
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.isComposing || e.keyCode === 229) return; // IME composition owns these keys
    const mod = isMac ? e.metaKey : e.ctrlKey;
    if (e.key === 'Escape' || (e.key === 'Enter' && mod)) {
      e.preventDefault();
      this.commit();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      return;
    }
    if (!mod || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key === 'b') { e.preventDefault(); this.exec('bold'); }
    else if (key === 'i') { e.preventDefault(); this.exec('italic'); }
    else if (key === 'k') { e.preventDefault(); this.openLink(); }
    else if (key === 'u') e.preventDefault(); // underline isn't part of the model
    else if (e.shiftKey && e.code === 'Digit8') { e.preventDefault(); this.exec('insertUnorderedList'); }
  }

  /** Plain text only: pasted HTML would bring styles the model can't represent. */
  private onPaste(e: ClipboardEvent): void {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    if (text && !text.startsWith(CLIPBOARD_OBJECTS)) this.exec('insertText', text);
  }

  private exec(command: string, value?: string): void {
    if (!this.session) return;
    document.execCommand(command, false, value);
    this.onInput();
    this.syncBar();
  }

  // --- formatting bar ---

  private positionBar(): void {
    const o = this.object();
    if (!o || this.bar.hidden) return;
    const cam = this.ed.camera;
    const r = aabb(o);
    const [left, top] = cam.worldToScreen(r.x, r.y);
    const [right, bottom] = cam.worldToScreen(r.x + r.w, r.y + r.h);
    const bw = this.bar.offsetWidth, bh = this.bar.offsetHeight;
    const vw = this.container.clientWidth, vh = this.container.clientHeight;
    const x = Math.min(Math.max(8, (left + right) / 2 - bw / 2), vw - bw - 8);
    let y = top - bh - 12;
    if (y < 8) y = Math.min(bottom + 12, vh - bh - 8);
    this.bar.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    if (!this.linkBox.hidden) {
      this.linkBox.style.transform = `translate(${Math.round(x)}px, ${Math.round(y + bh + 6)}px)`;
    }
  }

  private syncBar(): void {
    if (!this.session || this.bar.hidden) return;
    const set = (b: HTMLButtonElement, on: boolean) => {
      b.classList.toggle('active', on);
      b.setAttribute('aria-pressed', String(on));
    };
    set(this.buttons.bold, document.queryCommandState('bold'));
    set(this.buttons.italic, document.queryCommandState('italic'));
    set(this.buttons.list, document.queryCommandState('insertUnorderedList'));
    set(this.buttons.link, !!closestLink(getSelection()?.anchorNode ?? null, this.session.content));
  }

  // --- links ---

  private openLink(): void {
    const s = this.session;
    const sel = getSelection();
    if (!s || !sel || !sel.rangeCount) return;
    this.savedRange = sel.getRangeAt(0).cloneRange();
    const a = closestLink(sel.anchorNode, s.content);
    this.linkInput.value = a?.getAttribute('href') ?? '';
    this.linkBox.hidden = false;
    this.positionBar();
    this.linkInput.focus();
    this.linkInput.select();
  }

  private closeLink(refocus: boolean): void {
    if (this.linkBox.hidden) return;
    this.linkBox.hidden = true;
    const s = this.session;
    if (!refocus || !s) return;
    s.content.focus({ preventScroll: true });
    const sel = getSelection();
    if (sel && this.savedRange) {
      sel.removeAllRanges();
      sel.addRange(this.savedRange);
    }
  }

  private applyLink(): void {
    const s = this.session;
    const raw = this.linkInput.value.trim();
    const range = this.savedRange;
    this.closeLink(true);
    if (!s || !range) return;
    const existing = closestLink(range.startContainer, s.content);

    if (!raw) {
      if (existing) {
        const sel = getSelection()!;
        const r = document.createRange();
        r.selectNodeContents(existing);
        sel.removeAllRanges();
        sel.addRange(r);
        this.exec('unlink');
      }
      return;
    }
    const href = sanitizeHref(raw);
    if (!href) return;
    if (range.collapsed) {
      if (existing) {
        existing.setAttribute('href', href);
        this.onInput();
      } else {
        this.exec('insertHTML', linkHtml(href, raw));
      }
    } else {
      this.exec('createLink', href);
    }
  }
}

function combine(...fns: (() => void)[]): () => void {
  return () => { for (const f of fns) f(); };
}

function closestLink(node: Node | null, root: HTMLElement): HTMLAnchorElement | null {
  for (let n = node; n && n !== root; n = n.parentNode) {
    if (n instanceof HTMLAnchorElement) return n;
  }
  return null;
}

function placeCaret(content: HTMLElement, caret: CaretPlacement): void {
  const sel = getSelection();
  if (!sel) return;
  let range = typeof caret === 'object' ? rangeAtPoint(content, caret.clientX, caret.clientY) : null;
  if (!range && caret === 'all') {
    range = document.createRange();
    range.selectNodeContents(content);
  }
  range ??= endOfText(content);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Caret at the end of the last paragraph's text. Collapsing a range to the
 * container's end would leave it *after* the last paragraph element, where
 * IME composition misbehaves in Chrome.
 */
function endOfText(content: HTMLElement): Range {
  const r = document.createRange();
  let n: Node = content;
  for (;;) {
    const last: ChildNode | null = n.lastChild;
    if (!last) { r.setStart(n, 0); break; }
    if (last.nodeName === 'BR') { r.setStart(n, n.childNodes.length - 1); break; }
    if (last.nodeType === Node.TEXT_NODE) { r.setStart(last, last.textContent?.length ?? 0); break; }
    n = last;
  }
  r.collapse(true);
  return r;
}

/** Collapsed range at a screen point, if it lands inside `content`. */
function rangeAtPoint(content: HTMLElement, x: number, y: number): Range | null {
  // caretPositionFromPoint is standard; Safari (and older Chrome) only have caretRangeFromPoint.
  const doc = document as unknown as {
    caretPositionFromPoint?(x: number, y: number): CaretPosition | null;
    caretRangeFromPoint?(x: number, y: number): Range | null;
  };
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y);
    if (!p || !content.contains(p.offsetNode)) return null;
    const r = document.createRange();
    r.setStart(p.offsetNode, p.offset);
    r.collapse(true);
    return r;
  }
  const r = doc.caretRangeFromPoint?.(x, y) ?? null;
  return r && content.contains(r.startContainer) ? r : null;
}
